import {
    accessDelegationExample,
    accessImpersonationsExample,
    accessTokensExample,
    generateAccessDelegation,
    generateApiToken,
} from "@/mock-api/data/accessExample.ts";
import { billingAccountCardsExample } from "@/mock-api/data/billingAccountCardsExample.ts";
import {
    billingAccountsExample,
    metalBillingAccountsExample,
    nonSuspendedBillingAccountsExample,
} from "@/mock-api/data/billingAccountExample.ts";
import { billingAccountHistoryExample } from "@/mock-api/data/billingAccountHistoryExample.ts";
import { billingAccountInvoicesExample } from "@/mock-api/data/billingAccountInvoicesExample.ts";
import { billingAccountUsageExample } from "@/mock-api/data/billingAccountUsageExample.ts";
import { bootImagesExample } from "@/mock-api/data/bootImagesExample.ts";
import { campaignsExample } from "@/mock-api/data/campaignsExample.ts";
import { disksList } from "@/mock-api/data/disksExample.ts";
import { firewallList1, firewallList2 } from "@/mock-api/data/firewallsExample.ts";
import { googleTagManagerExample } from "@/mock-api/data/googleTagManagerExample.ts";
import { hostPools1, hostPoolsInvisible } from "@/mock-api/data/hostPoolsExample.ts";
import { generateIp, ipList1, ipList2 } from "@/mock-api/data/ipAddressesExample.ts";
import { generateLoadBalancer, lbList1, lbList2 } from "@/mock-api/data/loadBalancersExample.ts";
import {
    loc1Tll,
    loc2Jhv,
    loc3Jkt,
    loc4Trtu,
    multiLocationList,
    singleLocationList,
} from "@/mock-api/data/locationsExample.ts";
import {
    metalExampleConfig,
    metalExampleData,
    metalExampleLocationConfig,
    type MetalDbData,
    type MetalLocationDbData,
} from "@/mock-api/data/metalExample.ts";
import { vmParametersExample } from "@/mock-api/data/parametersExample.ts";
import {
    freePricingExample,
    largeNumbersRoundingPricingExample,
    manyDecimalDigitsRoundingPricingExample,
    missingPricingExample,
    pricingExample,
} from "@/mock-api/data/pricingExample.ts";
import { pricingHistoryExample } from "@/mock-api/data/pricingHistoryExample.ts";
import { referralAmountsExample } from "@/mock-api/data/referralAmountsExample.ts";
import { referralEnabledExample } from "@/mock-api/data/referralEnabledExample.ts";
import { s3UrlExample } from "@/mock-api/data/s3UrlExample.ts";
import { serviceImagesList } from "@/mock-api/data/serviceImagesExample.ts";
import { serviceSecretsExample, serviceWhitelistExample, servicesList } from "@/mock-api/data/servicesExample.ts";
import { sshKeysExample } from "@/mock-api/data/sshKeysExample.ts";
import { storageAccounts, storageList } from "@/mock-api/data/storageExample.ts";
import { storageKeys } from "@/mock-api/data/storageKeyExample.ts";
import { userExample } from "@/mock-api/data/userExample.ts";
import { generateVm, vmList1, vmList2 } from "@/mock-api/data/vmExample.ts";
import { vmImagesExample } from "@/mock-api/data/vmImagesExample.ts";
import {
    generateNetwork,
    vmInterfaces,
    vmInterfaces2,
    vpcList1,
    vpcList2,
} from "@/mock-api/data/vpcNetworksExample.ts";
import { demoDb } from "@/mock-api/demoDb.ts";
import { defaultMockDbOptions, type DefaultedMockDbOptions, type MockDbOptions } from "@/mock-api/mockDbVariants.ts";
import { randomArray } from "@/mock-api/randomTypes.ts";
import type {
    AccessDelegation,
    AccessImpersonation,
    ApiToken,
    BillingAccount,
    BillingAccountCard,
    BillingAccountHistory,
    BillingAccountInvoice,
    BillingAccountUsage,
    Bucket,
    Campaigns,
    Disk,
    Firewall,
    GoogleTagManagerKey,
    HostPool,
    IpAddress,
    LoadBalancer,
    Location,
    ManagedService,
    PricingHistory,
    PricingList,
    ReferralAmounts,
    ReferralEnabled,
    S3Url,
    ServiceImage,
    ServiceSecrets,
    ServiceWhiteList,
    SshKey,
    StorageAccount,
    StorageAccountKey,
    User,
    VirtualMachine,
    VmCustomImage,
    VmImage,
    VmInterface,
    VmParameter,
    Vpc,
} from "@warrenio/api-spec/spec.oats.gen";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";

/** NB: increment this when database schema/data changes (in {@link defaultDb}). */
export const VERSION = 35;

export interface MockDbData extends MetalDbData {
    locationsData: LocationsData;

    storageAccounts: StorageAccount[];
    storages: Bucket[];
    storageKeys: StorageAccountKey[];

    services: ManagedService[];
    serviceImages: ServiceImage[];
    serviceWhitelist: ServiceWhiteList;
    serviceSecrets: ServiceSecrets;

    tokens: ApiToken[];
    delegations: AccessDelegation[];
    impersonations: AccessImpersonation[];

    billingAccounts: BillingAccount[];
    billingCards: BillingAccountCard[];
    billingUsage: BillingAccountUsage[];
    billingHistory: BillingAccountHistory[];
    billingInvoices: BillingAccountInvoice[];

    vmImages: VmImage[];
    sshKeys: SshKey[];

    bootImages: VmCustomImage[];

    users: User[];

    campaigns: Campaigns[];
    referral_enabled: ReferralEnabled;
    referral_amounts: ReferralAmounts;
    gtm_key: GoogleTagManagerKey;
}

function emptyMetalDbData(): MetalDbData {
    return {
        ...metalExampleConfig,
    };
}

function emptyMetalLocationDbData(withConfig = false): MetalLocationDbData {
    return {
        metalSpecs: withConfig ? metalExampleLocationConfig.metalSpecs : [],
        metalMachines: [],
        metalLeases: [],
        metalStates: [],
        metalCommitments: [],
        metalCommitmentOffers: [],
    };
}

type LocationsData = Record<string, LocationSpecificData>;

export interface LocationSpecificData extends MetalLocationDbData {
    vms: VirtualMachine[];
    hostPools: HostPool[];

    ipAddresses: IpAddress[];
    vpcNetworks: Vpc[];
    vmInterfaces: VmInterface[];
    loadBalancers: LoadBalancer[];
    firewalls: Firewall[];
    disks: Disk[];
}

export interface MockDb extends MockDbData {
    VERSION: number;

    options: MockDbOptions;

    /** Has the data changed from the defaults? */
    modified: boolean;
    /** Next free available ID for a new object. */
    nextId: number;

    // Common data between all variants
    s3Url: S3Url;
    locations: Location[];
    pricing: PricingList;
    pricingHistory: PricingHistory;
    vmParameters: VmParameter[];
}

function makeBaseDb(options: DefaultedMockDbOptions) {
    const { singleLocation } = options;
    return {
        VERSION,
        options,
        modified: false,
        // Start at a large number to avoid collisions with constant example data's IDs.
        nextId: 10000,

        // Base data
        s3Url: s3UrlExample,
        locations: singleLocation ? singleLocationList : multiLocationList,
        pricing: makePricingVariant(options),
        pricingHistory: pricingHistoryExample,
        vmParameters: vmParametersExample,
    } satisfies Partial<MockDb>;
}

function defaultDb(options: DefaultedMockDbOptions) {
    return {
        locationsData: {
            [loc1Tll]: {
                ...emptyMetalLocationDbData(true),
                vms: vmList1,
                ipAddresses: ipList1,
                vpcNetworks: vpcList1,
                loadBalancers: lbList1,
                vmInterfaces: vmInterfaces,
                firewalls: firewallList1,
                hostPools: makeHostPools(hostPools1, options),
                disks: disksList,
            },
            [loc2Jhv]: {
                ...emptyMetalLocationDbData(),
                vms: vmList2,
                ipAddresses: ipList2,
                vpcNetworks: vpcList2,
                loadBalancers: lbList2,
                vmInterfaces: vmInterfaces2,
                firewalls: firewallList2,
                hostPools: hostPoolsInvisible,
                disks: [],
            },
            [loc3Jkt]: makeEmptyLocation(),
            [loc4Trtu]: makeEmptyLocation(),
        },
        storageAccounts: storageAccounts,
        storages: storageList,
        storageKeys,
        services: servicesList,
        serviceImages: serviceImagesList,
        serviceWhitelist: serviceWhitelistExample,
        serviceSecrets: serviceSecretsExample,
        tokens: accessTokensExample,
        delegations: accessDelegationExample,
        impersonations: accessImpersonationsExample,
        billingAccounts: makeBillingAccounts(options),
        billingCards: billingAccountCardsExample,
        billingUsage: billingAccountUsageExample,
        billingHistory: billingAccountHistoryExample,
        billingInvoices: billingAccountInvoicesExample,
        vmImages: vmImagesExample,
        sshKeys: sshKeysExample,
        bootImages: bootImagesExample,
        users: [userExample],
        campaigns: campaignsExample,
        referral_enabled: referralEnabledExample,
        referral_amounts: referralAmountsExample,
        gtm_key: googleTagManagerExample,
        ...emptyMetalDbData(),
    } satisfies MockDbData;
}

function makeBillingAccounts({ billingVariant }: DefaultedMockDbOptions) {
    switch (billingVariant) {
        case "all":
            return billingAccountsExample;
        case "notSuspended":
            return nonSuspendedBillingAccountsExample;
        default:
            exhaustiveSwitchCheck(billingVariant);
    }
}

function makeEmptyLocation(isDefault = false, withMetalConfig = false) {
    return {
        //#region System data
        // NB: Empty location must still have host pools, otherwise eg. Service Create view is broken
        // XXX: need to generate unique UUIDs per location, so only do default location right now
        hostPools: isDefault ? hostPoolsInvisible : [],
        //#endregion

        vms: [],
        ipAddresses: [],
        vpcNetworks: [],
        loadBalancers: [],
        vmInterfaces: [],
        firewalls: [],
        disks: [],
        ...emptyMetalLocationDbData(withMetalConfig),
    } satisfies LocationSpecificData;
}

function makeHostPools(basePools: HostPool[], { hostPools }: MockDbOptions) {
    switch (hostPools) {
        case "none":
            return [];
        case "single":
            return [basePools[0]];
        case "multi":
        case undefined:
            return basePools;
        default:
            exhaustiveSwitchCheck(hostPools);
    }
}

function emptyDb() {
    return {
        //#region System data and configuration
        locationsData: {
            [loc1Tll]: makeEmptyLocation(true, true),
            [loc2Jhv]: makeEmptyLocation(),
            // [loc3Jkt]: makeEmptyLocation(),
            // [loc4Trtu]: makeEmptyLocation(),
        },
        // Must have service images for service creation to work
        serviceImages: serviceImagesList,
        // We want at least one VM image for VM creation UI to work
        vmImages: vmImagesExample.slice(0, 1),

        referral_enabled: referralEnabledExample,
        referral_amounts: referralAmountsExample,
        gtm_key: { gtm_key: "" },
        campaigns: [],
        //#endregion

        users: [userExample],

        storageAccounts: [],
        storages: [],
        storageKeys: [],
        services: [],
        serviceWhitelist: [],
        serviceSecrets: { sql_pass: "" },
        tokens: [],
        delegations: [],
        impersonations: [],
        billingAccounts: [],
        billingCards: [],
        billingUsage: [],
        billingHistory: [],
        billingInvoices: [],
        sshKeys: [],
        bootImages: [],
        ...emptyMetalDbData(),
    } satisfies MockDbData;
}

function makePricingVariant({ pricingVariant }: DefaultedMockDbOptions) {
    switch (pricingVariant) {
        case "default":
            return pricingExample;
        case "free_prices":
            return freePricingExample;
        case "missing_prices":
            return missingPricingExample;
        case "large_numbers_rounding":
            return largeNumbersRoundingPricingExample;
        case "many_decimal_digits":
            return manyDecimalDigitsRoundingPricingExample;
        default:
            exhaustiveSwitchCheck(pricingVariant);
    }
}

function bigDb(options: DefaultedMockDbOptions, count = 100) {
    const baseDb = defaultDb(options);

    const makeRandomLocation = (slug: keyof typeof baseDb.locationsData) => {
        const perLocationCount = Math.ceil(count / 2);
        const networkCount = Math.ceil(perLocationCount / 3);

        const baseLocation = baseDb.locationsData[slug];
        return {
            ...baseLocation,

            vms: randomArray(perLocationCount, generateVm),
            ipAddresses: randomArray(networkCount, generateIp),
            vpcNetworks: randomArray(networkCount, () => generateNetwork(baseLocation.vpcNetworks)),
            loadBalancers: slug === loc1Tll ? randomArray(networkCount, generateLoadBalancer) : [],
        };
    };

    return {
        ...baseDb,

        // Set as modified so it is not regenerated on page reload, since due to randomness it would have different data every time.
        modified: true,

        locationsData: {
            [loc1Tll]: makeRandomLocation(loc1Tll),
            [loc2Jhv]: makeRandomLocation(loc2Jhv),
            [loc3Jkt]: makeEmptyLocation(),
            [loc4Trtu]: makeEmptyLocation(),
        },

        tokens: randomArray(count, generateApiToken),
        delegations: randomArray(count, generateAccessDelegation),
    } satisfies MockDbData & Partial<MockDb>;
}

function metalEmptyDb(_options: DefaultedMockDbOptions) {
    return {
        ...emptyDb(),
    } satisfies MockDbData;
}

function metalDb(options: DefaultedMockDbOptions) {
    const db = metalEmptyDb(options);
    return {
        ...db,
        campaigns: campaignsExample,
        // XXX: Ignores options.billingVariant
        billingAccounts: metalBillingAccountsExample,
        billingCards: billingAccountCardsExample,
        locationsData: {
            ...db.locationsData,
            [loc1Tll]: {
                ...db.locationsData[loc1Tll],
                ...metalExampleLocationConfig,
                ...metalExampleData,
            },
        },
    } satisfies MockDbData;
}

export function buildMockDb(options: MockDbOptions = {}): MockDb {
    const defaultedOptions = { ...defaultMockDbOptions, ...options };
    const { variant = "default" } = defaultedOptions;

    if (variant === "metal") {
        // XXX: Hack to make the metal variant always single location, think of a better way
        defaultedOptions.singleLocation = true;
    }

    const base = makeBaseDb(defaultedOptions);

    switch (variant) {
        case "default":
            return { ...base, ...defaultDb(defaultedOptions) };
        case "empty":
            return { ...base, ...emptyDb() };
        case "unauthorized":
            return { ...base, ...defaultDb(defaultedOptions) };
        case "big":
            return { ...base, ...bigDb(defaultedOptions) };

        case "metal":
            return { ...base, ...metalDb(defaultedOptions) };
        case "metal_empty":
            return { ...base, ...metalEmptyDb(defaultedOptions) };

        case "demo":
            // XXX: demo data mock for locations
            base.locations[0].display_name = "New York";
            base.locations[0].country_code = "usa";
            base.locations[1].display_name = "Washington";
            base.locations[1].country_code = "usa";
            base.locations[2].display_name = "Sydney";
            base.locations[2].country_code = "aus";

            return { ...base, ...demoDb() };

        default:
            exhaustiveSwitchCheck(variant);
    }
}
